import React from 'react';

import { Props as TooltipProps, TooltipContent } from './tooltip-content';
import { TooltipContext } from './tooltip-context';
import { TooltipTrigger } from './tooltip-trigger';
import { useTooltip, UseTooltipProps } from './use-tooltip';

type Props = {
  children?: React.ReactElement;
  content: React.ReactNode;
  title?: string;
  transitionDelay?: TooltipProps['transitionDuration'];
  /**
   * Wraps children with <div />. Can be useful when ref in children root component not referring to
   * the entire component (from which tooltip should be triggered when hovering the mouse). For
   * instance <CheckBox /> where forwarding ref points to the checkbox and not the entire component
   * (including checkbox text for instance).
   */
  wrapChildren?: boolean;
} & UseTooltipProps;

/**
 * Displays a tooltip when hovering the children prop. Make sure the children prop exposes a ref of
 * the DOM element that should trig the tooltip.
 */
const Tooltip = ({ wrapChildren = false, ...props }: Props) => {
  const tooltip = useTooltip(props);

  return (
    <TooltipContext.Provider value={tooltip}>
      <TooltipContent title={props.title} transitionDuration={props.transitionDelay}>
        {props.content}
      </TooltipContent>
      <TooltipTrigger>{wrapChildren ? <div>{props.children}</div> : props.children}</TooltipTrigger>
    </TooltipContext.Provider>
  );
};

export { Tooltip, Props as TooltipProps };
